import {
  createAsyncThunk,
  EntityState,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import { Attorney, UsersService } from '@app/gen/attorneys';
import { ThunkExtra } from '@app/store';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { SettingsState } from '../../settings.state';
import { SettingsWithRootState } from '../..';

export const getAttorney = createAsyncThunk<
  Attorney,
  { id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'attorneys/getAttorney',
  async ({ id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(UsersService);

      const result = await service.attorneyGet({
        params: { attorney_id: id },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAttorneyDetailReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAttorney.pending, (state, { meta }) => {
    state.attorneyDetail.currentId = meta.requestId;
    state.attorneyDetail.status = 'loading';
    state.attorneyDetail.data = null;
    state.attorneyDetail.error = null;
  });
  builder.addCase(getAttorney.fulfilled, (state, { payload, meta }) => {
    if (meta.requestId === state.attorneyDetail.currentId) {
      state.attorneyDetail.status = 'complete';
      state.attorneyDetail.data = payload;
      state.attorneyDetail.error = null;
      state.attorneyDetail.currentId = null;
    }
  });
  builder.addCase(getAttorney.rejected, (state, { payload, error, meta }) => {
    if (meta.requestId === state.attorneyDetail.currentId) {
      state.attorneyDetail.status = 'failed';
      state.attorneyDetail.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.attorneyDetail.currentId = null;
    }
  });
};
